(() => {
  'use strict';
  const configNode = document.getElementById('monzoro-pocket-offer-config');
  if (!configNode || window.MonzoroPocketOffer) return;
  const config = JSON.parse(configNode.textContent);
  const nativeFetch = window.fetch.bind(window);
  const storageKey = 'monzoro-pocket-offer-recovery-v1';
  let chain = Promise.resolve();
  let current = null;
  let busy = false;
  let failure = '';
  let drawer = null;
  let returnFocus = null;
  let closeEpoch = 0;
  document.documentElement.classList.add('mnz-cart-enabled');
  const text = (key, replacements = {}) => Object.entries(replacements).reduce((value,[name,replacement]) => value.replaceAll(`__${name}__`,String(replacement)),config.labels[key] || key);
  const money = cents => new Intl.NumberFormat('en-PK',{style:'currency',currency:config.currency,maximumFractionDigits:2}).format(cents/100);
  const clean = properties => Object.fromEntries(Object.entries(properties || {}).filter(([key]) => !key.startsWith('_essenify_') && !key.startsWith('_monzoro_pw_')));
  const idOf = item => `${item.id}:${JSON.stringify(Object.entries(clean(item.properties)).sort())}`;
  const children = item => item.components.length ? item.components : [item];
  const auto = item => item.properties?._monzoro_pw_mode === 'native';
  const ownParent = item => item.id===config.parent && item.components.length && item.components.every(child=>auto(child) || (child.eligible && String(child.properties?._essenify_mam_original_bundle_id)==='302'));
  const pool = state => state.items.flatMap(item => {
    if(item.components.length) return ownParent(item) ? item.components : [];
    return (item.eligible && !item.properties?._essenify_mam_bundle_id) || auto(item) ? [item] : [];
  });
  const roots = state => state.items.filter(item => item.components.length ? ownParent(item) : (item.eligible && !item.properties?._essenify_mam_bundle_id) || auto(item));
  const quote = rows => {
    const prices = rows.filter(item => item.eligible).flatMap(item => Array(Math.max(0,item.quantity-(item.paired||0))).fill(item.price)).sort((a,b)=>a-b);
    const prefix = [0];prices.forEach(price => prefix.push(prefix[prefix.length-1]+price));
    let best = {total:prefix.at(-1),five:0,three:0,extra:prices.length,count:prices.length,original:prefix.at(-1)};
    for(let five=0;five<=Math.floor(prices.length/5);five++) for(let three=0;three<=Math.floor((prices.length-five*5)/3);three++) {
      const extra = prices.length-five*5-three*3;
      const total = five*600000+three*400000+prefix[extra];
      if(total<best.total) best = {...best,total,five,three,extra};
    }
    return best;
  };
  const stateFrom = html => {
    const node = new DOMParser().parseFromString(html,'text/html').querySelector('[data-wardrobe-cart-state]');
    if(!node) throw new Error(text('unavailable'));
    return JSON.parse(node.textContent);
  };
  const read = async () => {
    const response = await nativeFetch(`${config.root}cart?section_id=monzoro-pocket-cart-state`,{cache:'no-store'});
    if(!response.ok) throw new Error(text('unavailable'));
    return stateFrom(await response.text());
  };
  const request = async (endpoint, body) => {
    const response = await nativeFetch(config.root+endpoint,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)});
    const result = await response.json();
    if(!response.ok) throw new Error(result.description || result.message || text('unavailable'));
    return result;
  };
  const compact = rows => {
    const groups = new Map();
    for(const row of rows) {
      const key=idOf(row);
      if(!groups.has(key)) groups.set(key,{...row,properties:clean(row.properties),quantity:0});
      groups.get(key).quantity+=row.quantity;
    }
    return [...groups.values()].filter(row=>row.quantity>0);
  };
  // Preserve exact native matching-set quantities; only surplus squares enter a pack.
  const allocate = (state, rows = compact(pool(state))) => {
    const available = new Map();
    if(config.currency==='PKR')for(const rule of config.matchingRules || []){
      const quantity=state.items.filter(item=>!item.components.length && !item.sellingPlan && item.product===rule.primary).reduce((sum,item)=>sum+item.quantity,0);
      if(quantity)available.set(rule.square,(available.get(rule.square)||0)+quantity);
    }
    return rows.map(row=>{
      const paired=row.eligible?Math.min(row.quantity,available.get(row.product)||0):0;
      if(paired)available.set(row.product,available.get(row.product)-paired);
      return {...row,paired};
    });
  };
  const signature = rows => JSON.stringify(compact(rows).map(row=>[idOf(row),row.quantity,row.price,row.eligible]).sort((a,b)=>a[0].localeCompare(b[0])));
  const planItems = (rows, transaction) => {
    const offer=quote(rows);
    const chosen=rows.filter(row=>row.eligible && row.quantity>(row.paired||0)).map(row=>({...row,quantity:row.quantity-(row.paired||0)}));
    const selected=JSON.stringify(chosen.map(row=>({variant_id:String(row.id),quantity:row.quantity})));
    const group='302_native_'+transaction;
    return rows.flatMap(row=>{
      const result=[];
      const paired=row.paired||0;
      const properties={...clean(row.properties),_monzoro_pw_mode:'native',_monzoro_pw_transaction:transaction};
      if(paired)result.push({id:row.id,quantity:paired,properties:{...properties,_monzoro_pw_allocation:'matching'}});
      if(row.quantity>paired)result.push({id:row.id,quantity:row.quantity-paired,properties:{...properties,...(row.eligible && offer.count>=3 ? {
        _essenify_mam_bundle_id:group,
        _essenify_mam_original_bundle_id:'302',
        _essenify_mam_parent_variant_id:`gid://shopify/ProductVariant/${config.parent}`,
        _essenify_mam_discount_percentage:(1-offer.total/offer.original)*100,
        _essenify_mam_per_bundle_qty:row.quantity-paired,
        _essenify_mam_items:selected
      } : {})}});
      return result;
    });
  };
  const save = record => {try{sessionStorage.setItem(storageKey,JSON.stringify(record));}catch{throw new Error(text('storage_error'));}};
  const pending = () => {try{return JSON.parse(sessionStorage.getItem(storageKey)||'null');}catch{return null;}};
  const finished = () => sessionStorage.removeItem(storageKey);
  const enqueue = action => {
    const operation=chain.catch(()=>{}).then(()=>navigator.locks ? navigator.locks.request('monzoro-pocket-cart',action) : action());
    chain=operation;return operation;
  };
  const matches = (state,rows) => {
    if(signature(pool(state))!==signature(rows))return false;
    const allocated=allocate(state,rows);
    const offer=quote(allocated);
    const packed=roots(state).filter(item=>item.components.length);
    if(offer.count<3)return packed.length===0;
    const expected=allocated.filter(row=>row.eligible && row.quantity>(row.paired||0)).map(row=>({...row,quantity:row.quantity-(row.paired||0)}));
    return packed.length===1 && packed[0].id===config.parent &&
      signature(packed.flatMap(item=>item.components))===signature(expected) && Math.abs(packed[0].final-offer.total)<1;
  };
  const restore = async record => {
    const state=await read();
    const managed=state.items.filter(item=>children(item).every(row=>row.properties?._monzoro_pw_transaction===record.transaction));
    if(managed.length) await request('cart/update.js',{updates:Object.fromEntries(managed.map(item=>[item.key,0]))});
    const fresh=await read();
    const originalStillPresent=new Map(roots(fresh).map(item=>[item.key,item]));
    const remaining=record.beforeRoots.filter(item=>originalStillPresent.has(item.key));
    if(remaining.length) await request('cart/update.js',{updates:Object.fromEntries(remaining.map(item=>[item.key,0]))});
    if(record.beforeRaw.length) await request('cart/add.js',{items:record.beforeRaw.map(row=>({id:row.id,quantity:row.quantity,properties:row.properties}))});
    const restored=await read();
    if(signature(pool(restored))!==signature(record.before)) throw new Error(text('restore_failed'));
    finished();return restored;
  };
  const rebuild = async (state,desired) => {
    const before=compact(pool(state));
    const target=allocate(state,compact(desired));
    const variantQuantities=new Map();
    target.forEach(row=>variantQuantities.set(row.id,(variantQuantities.get(row.id)||0)+row.quantity));
    for(const row of target) {
      if(!Number.isInteger(row.quantity)||row.quantity<1) throw new Error(text('quantity_error'));
      if(row.stock!==null && variantQuantities.get(row.id)>row.stock) throw new Error(text('stock_error',{title:row.title,quantity:row.stock}));
    }
    const transaction=crypto.randomUUID().replaceAll('-','');
    const beforeRoots=roots(state);
    const record={transaction,before,beforeRaw:pool(state),beforeRoots,target,time:Date.now()};
    save(record);
    try {
      if(beforeRoots.length) await request('cart/update.js',{updates:Object.fromEntries(beforeRoots.map(item=>[item.key,0]))});
      if(target.length) await request('cart/add.js',{items:planItems(target,transaction)});
      const after=await read();
      if(!matches(after,target)) throw new Error(text('price_error'));
      finished();return after;
    } catch(error) {
      try {current=await restore(record);} catch {throw new Error(`${error.message} ${text('restore_failed')}`);}
      throw new Error(`${error.message} ${text('restored')}`);
    }
  };
  const reconcile = async state => {
    const rows=allocate(state);
    if(!rows.length) return state;
    const offer=quote(rows);
    const alreadyNative=pool(state).every(auto);
    if(alreadyNative && matches(state,rows)) return state;
    if(offer.count<3 && !pool(state).some(row=>row.properties?._essenify_mam_bundle_id)) return state;
    return rebuild(state,rows);
  };
  const ensure = () => enqueue(async()=>{
    setBusy(true);
    try {
      const recovery=pending();
      const state=recovery ? await restore(recovery) : await read();
      current=await reconcile(state);failure='';render();return current;
    } catch(error) {failure=error.message;render();throw error;}
    finally{setBusy(false);}
  });
  const element = (tag,className,value) => {
    const node=document.createElement(tag);if(className)node.className=className;if(value!==undefined)node.textContent=value;return node;
  };
  const button = (label,action,className) => {
    const node=element('button',className,label);node.type='button';node.addEventListener('click',action);return node;
  };
  const setBusy = value => {
    busy=value;
    document.querySelectorAll('[data-pocket-cart-content]').forEach(root=>{
      root.setAttribute('aria-busy',String(value));
      root.querySelectorAll('button:not([data-pocket-close]),input,textarea').forEach(node=>node.disabled=value || node.hasAttribute('data-pocket-stock-limit') || (node.matches('.MnzPocketCart__checkout') && (!!failure || !!pending())));
      const status=root.querySelector('[data-pocket-status]');if(status)status.textContent=value ? text('updating') : failure;
    });
  };
  const change = (row,quantity,isPool) => enqueue(async()=>{
    setBusy(true);
    try{
      const state=await read();
      if(isPool){
        const target=compact(pool(state));
        const found=target.find(item=>idOf(item)===idOf(row));
        if(!found)throw new Error(text('cart_changed'));
        found.quantity=quantity;
        current=await rebuild(state,target.filter(item=>item.quantity>0));
      }else{
        const line=state.items.find(item=>item.key===row.key);
        if(!line)throw new Error(text('cart_changed'));
        await request('cart/change.js',{id:line.key,quantity});
        current=await reconcile(await read());
      }
      failure='';render();
    }catch(error){failure=error.message;current=await read().catch(()=>current);render();}
    finally{setBusy(false);}
  });
  const checkout = async()=>{
    try{await ensure();if(!pending())location.assign(config.root+'checkout');}catch{}
  };
  const release = () => {
    document.documentElement.classList.remove('mnz-pocket-cart-open','mnz-pocket-cart-opening');
    if(returnFocus?.isConnected)returnFocus.focus({preventScroll:true});
  };
  const close = ()=>{
    closeEpoch++;
    if(drawer?.open)drawer.close();
    release();
  };
  const saveNote = value => enqueue(async()=>{
    setBusy(true);
    try{await request('cart/update.js',{note:value});current=await read();failure='';render();}
    catch(error){failure=error.message;render();}
    finally{setBusy(false);}
  });
  // Display only: keep every paise of Shopify's returned bundle line total.
  const splitAmount = (total, weights) => {
    if(!weights.length)return [];
    const safe=weights.map(value=>Number.isSafeInteger(value)&&value>0?value:0);
    if(!safe.some(Boolean))safe.fill(1);
    const sum=BigInt(safe.reduce((a,b)=>a+b,0));
    const amounts=safe.map((weight,index)=>{
      const numerator=BigInt(total)*BigInt(weight);
      return {index,amount:Number(numerator/sum),remainder:numerator%sum};
    });
    let remaining=total-amounts.reduce((sum,row)=>sum+row.amount,0);
    const order=[...amounts].sort((a,b)=>a.remainder===b.remainder?a.index-b.index:a.remainder>b.remainder?-1:1);
    for(const row of order){if(!remaining)break;row.amount++;remaining--;}
    return amounts.map(row=>row.amount);
  };
  const cartLinePrices = state => {
    const result=new Map();
    const managed=new Set(roots(state).map(row=>row.key));
    const reserved=new Map(allocate(state).map(row=>[idOf(row),row.paired]));
    const add=(row,final,kind)=>{
      const key=idOf(row),original=row.price*row.quantity;
      if(!result.has(key))result.set(key,{original:0,final:0,quantity:0,parts:[]});
      const price=result.get(key);
      price.original+=original;price.final+=final;price.quantity+=row.quantity;
      const part=price.parts.find(part=>part.kind===kind && part.original*row.quantity===original*part.quantity && part.final*row.quantity===final*part.quantity);
      if(part){part.quantity+=row.quantity;part.original+=original;part.final+=final;}
      else price.parts.push({original,final,quantity:row.quantity,kind,discounts:[]});
      const target=part || price.parts[price.parts.length-1];
      for(const discount of row.discounts || []){
        const existing=target.discounts.find(item=>item.title===discount.title);
        if(existing)existing.amount+=discount.amount;
        else target.discounts.push({...discount});
      }
    };
    for(const row of state.items){
      if(!managed.has(row.key))continue;
      if(row.components.length){
        const components=row.components;
        const amounts=components.map(child=>Number.isSafeInteger(child.final)?child.final:child.price*child.quantity);
        const totals=amounts.reduce((sum,amount)=>sum+amount,0)===row.final?amounts:splitAmount(row.final,amounts);
        components.forEach((child,index)=>add(child,totals[index],'pack'));
      }else{
        const matching=row.properties?._monzoro_pw_allocation==='matching' || (reserved.get(idOf(row)) && row.final<row.price*row.quantity);
        add(row,row.final,matching?'matching':'standard');
      }
    }
    return result;
  };
  const pricePair = (original,final) => {
    const prices=element('span','MnzPocketCart__price-pair');
    if(final<original)prices.classList.add('MnzPocketCart__price-pair--sale');
    const now=element(final<original?'strong':'span','MnzPocketCart__price-current',money(final));
    now.setAttribute('aria-label',`${text(final<original?'price_offer':'price_current')} ${money(final)}`);prices.append(now);
    if(final<original){
      const was=element('s','MnzPocketCart__price-original',money(original));
      was.setAttribute('aria-label',`${text('price_original')} ${money(original)}`);prices.append(was);
    }
    return prices;
  };
  const unitPrices = price => {
    const container=element('div','MnzPocketCart__unit-price');
    for(const part of price.parts){
      const band=element('div','MnzPocketCart__price-band');
      if(price.parts.length>1)band.append(element('span','MnzPocketCart__price-caption',`${text('price_'+part.kind)} · ${part.quantity} ×`));
      band.append(pricePair(part.original/part.quantity,part.final/part.quantity));
      if(part.quantity>1)band.append(element('span','MnzPocketCart__price-caption',text(part.final%part.quantity?'price_average':'price_each')));
      container.append(band);
    }
    return container;
  };
  const discountBadges = price => {
    const list=element('ul','MnzPocketCart__discount-list');
    const append=(title,amount)=>{
      const badge=element('li','MnzPocketCart__discount');
      const icon=document.createElementNS('http://www.w3.org/2000/svg','svg');
      icon.setAttribute('viewBox','0 0 16 16');icon.setAttribute('aria-hidden','true');icon.setAttribute('focusable','false');
      const path=document.createElementNS('http://www.w3.org/2000/svg','path');
      path.setAttribute('fill','currentColor');path.setAttribute('d','M1 1h6l8 8-6 6-8-8V1zm3 2a1 1 0 1 0 0 2 1 1 0 0 0 0-2zm3.3 2.3L6.2 6.4l4.4 4.4 1.1-1.1-4.4-4.4z');icon.append(path);
      badge.append(icon,element('span','',`${title}: −${money(amount)}`));list.append(badge);
    };
    for(const part of price.parts){
      const saving=part.original-part.final;
      if(saving<=0)continue;
      const discounts=(part.discounts||[]).filter(discount=>discount.amount>0);
      const allocated=discounts.reduce((sum,discount)=>sum+discount.amount,0);
      if(discounts.length && allocated<=saving){
        discounts.forEach(discount=>append(discount.title,discount.amount));
        if(saving>allocated)append(text(part.kind==='pack'?'price_pack':part.kind==='matching'?'price_matching':'price_discount'),saving-allocated);
      }else append(text(part.kind==='pack'?'price_pack':part.kind==='matching'?'price_matching':'price_discount'),saving);
    }
    return list;
  };
  const content = root => {
    root.replaceChildren();root.dataset.pocketCartContent='';
    const full=!!root.closest('.MnzPocketFullCart');
    const head=element('div','MnzPocketCart__header');
    const rows=current ? allocate(current) : [];
    const other=current ? current.items.filter(item=>!roots(current).some(root=>root.key===item.key)) : [];
    const count=rows.reduce((sum,row)=>sum+row.quantity,0)+other.reduce((sum,item)=>sum+item.quantity,0);
    const title=element('h2','',current&&!full?`${text('cart')} · ${count}`:text('cart'));title.id=root.id+'-title';head.append(title);
    if(root.closest('dialog')){const exit=button('×',close,'MnzPocketCart__close');exit.dataset.pocketClose='';exit.setAttribute('aria-label',text('close'));head.append(exit);}
    root.append(head);
    const status=element('p','MnzPocketCart__status',failure || (!current?text('updating'):''));status.dataset.pocketStatus='';status.setAttribute('role','status');status.setAttribute('aria-live','polite');root.append(status);
    if(failure){root.append(button(text('retry'),()=>ensure().catch(()=>{}),'MnzPocketCart__retry'));}
    if(!current)return;
    const offer=quote(rows);
    if(offer.count){
      const intro=element('div','MnzPocketCart__offer');
      intro.append(element('p','MnzPocketCart__offer-title',offer.count>=3?text('applied'):text('offer')));
      intro.append(element('p','',offer.count>=3?[offer.five?text('five_packs',{quantity:offer.five}):'',offer.three?text('three_packs',{quantity:offer.three}):'',offer.extra?text('extras',{quantity:offer.extra}):''].filter(Boolean).join(' · '):text('progress',{quantity:3-offer.count})));
      intro.append(element('small','',text('eligibility')));root.append(intro);
    }
    const list=element('div','MnzPocketCart__items');
    const prices=cartLinePrices(current);
    const display=[...rows.map(row=>({row,isPool:true})),...other.map(row=>({row,isPool:false}))];
    if(full && display.length){
      const columns=element('div','MnzPocketCart__columns');columns.setAttribute('aria-hidden','true');
      columns.append(element('span','MnzPocketCart__column-product',text('column_product')),element('span','MnzPocketCart__column-quantity',text('column_quantity')),element('span','MnzPocketCart__column-total',text('column_total')));root.append(columns);
    }
    if(!display.length)list.append(element('p','MnzPocketCart__empty',text('empty')));
    for(const {row,isPool} of display){
      const line=element('article','MnzPocketCart__item');
      const link=element('a','MnzPocketCart__image');link.href=row.url;
      if(row.image){const img=element('img');img.src=row.image;img.alt=row.title;img.width=88;img.height=88;link.append(img);}
      const body=element('div','MnzPocketCart__item-body');
      const name=element('a','MnzPocketCart__name',row.title);name.href=row.url;body.append(name);
      if(row.variant && row.variant!=='Default Title')body.append(element('p','MnzPocketCart__variant',row.variant));
      const original=isPool?row.price*row.quantity:(row.original??row.price*row.quantity);
      const price=isPool?prices.get(idOf(row)):{original,final:row.final,quantity:row.quantity,parts:[{original,final:row.final,quantity:row.quantity,kind:'standard',discounts:row.discounts||[]}]};
      body.append(unitPrices(price));
      body.append(discountBadges(price));
      if(!isPool && row.components.length){const details=element('ul','MnzPocketCart__components');row.components.forEach(child=>details.append(element('li','',`${child.quantity} × ${child.title}`)));body.append(details);}
      const controls=element('div','MnzPocketCart__quantity');
      const minus=button('−',()=>change(row,row.quantity-1,isPool));minus.setAttribute('aria-label',text('decrease',{title:row.title}));
      const qty=element('span','',String(row.quantity));qty.setAttribute('aria-label',text('quantity',{quantity:row.quantity}));
      const plus=button('+',()=>change(row,row.quantity+1,isPool));plus.setAttribute('aria-label',text('increase',{title:row.title}));
      if(row.stock!==null && row.quantity>=row.stock){plus.dataset.pocketStockLimit='';plus.disabled=true;plus.title=text('stock_error',{title:row.title,quantity:row.stock});}
      controls.append(minus,qty,plus);
      const actions=element('div','MnzPocketCart__item-controls');actions.append(controls);
      const end=element('div','MnzPocketCart__item-end');
      const remove=button(text('remove_label'),()=>change(row,0,isPool),'MnzPocketCart__remove');remove.setAttribute('aria-label',text('remove',{title:row.title}));const linePrice=pricePair(price.original,price.final);linePrice.classList.add('MnzPocketCart__line-price');actions.append(remove);end.append(linePrice);
      line.append(link,body,actions,end);list.append(line);
    }
    root.append(list);
    if(display.length){
      const footer=element('div','MnzPocketCart__footer');
      if(config.notesEnabled){
        const details=element('details','MnzPocketCart__notes');details.open=!!current.note;
        details.append(element('summary','MnzPocketCart__note-label',text('note')));
        const note=element('textarea','MnzPocketCart__note');note.rows=2;note.value=current.note||'';note.setAttribute('aria-label',text('note'));
        note.addEventListener('change',()=>saveNote(note.value));
        details.append(note);footer.append(details);
      }
      const saving=offer.original-offer.total;
      if(saving>0){const line=element('p','MnzPocketCart__total-line');line.append(element('span','',text('saving')),element('span','',`−${money(saving)}`));footer.append(line);}
      if(current.discount>0){const line=element('p','MnzPocketCart__total-line');line.append(element('span','',text('other_discounts')),element('span','',`−${money(current.discount)}`));footer.append(line);}
      const total=element('p','MnzPocketCart__total-line MnzPocketCart__subtotal');total.append(element('strong','',text('subtotal')),element('strong','',money(current.total)));footer.append(total);
      footer.append(element('p','MnzPocketCart__shipping',text('shipping')));
      const pay=button(text('checkout'),checkout,'MnzPocketCart__checkout');pay.disabled=busy||!!failure||!!pending();footer.append(pay);
      root.append(footer);
    }
    setBusy(busy);
  };
  const render = ()=>{
    document.documentElement.classList.add('mnz-pocket-cart-managed');
    document.querySelectorAll('.Header__CartDot').forEach(dot=>dot.classList.toggle('is-visible',!!current?.count));
    document.querySelectorAll('[data-pocket-cart-content]').forEach(content);
    const full=document.querySelector('[data-pocket-full-cart]');
    if(full && current){
      const main=document.getElementById('main');
      const old=main?.querySelector('[data-mnz-cart-fallback]');
      const anchor=old?.closest('.shopify-section') || old;
      if(anchor && anchor.previousElementSibling!==full)anchor.before(full);
      if(anchor){anchor.hidden=true;anchor.dataset.pocketNativeHidden='';}
      document.documentElement.classList.add('mnz-pocket-full-active');
      full.hidden=false;content(full);
    }
  };
  const open = async()=>{
    if(!drawer?.open)returnFocus=document.activeElement;
    if(!drawer){
      drawer=element('dialog','MnzPocketCart');drawer.id='monzoro-pocket-cart';
      drawer.setAttribute('aria-labelledby','monzoro-pocket-cart-content-title');
      const inner=element('div');inner.id='monzoro-pocket-cart-content';inner.dataset.pocketCartContent='';drawer.append(inner);
      drawer.addEventListener('click',event=>{if(event.target===drawer)close();});
      drawer.addEventListener('cancel',event=>{event.preventDefault();close();});
      drawer.addEventListener('close',release);
      document.body.append(drawer);
    }
    document.documentElement.classList.remove('mnz-pocket-cart-opening');
    document.documentElement.classList.add('mnz-pocket-cart-open');
    if(!drawer.open)drawer.showModal();
    content(drawer.firstElementChild);
    try{await ensure();}catch{}
  };
  window.MonzoroPocketOffer={read,quote,ensure,open,change,get state(){return current;}};
  window.fetch=async(input,options)=>{
    const url=new URL(typeof input==='string'?input:input instanceof URL?input.href:input.url,location.href);
    const method=String(options?.method || (input instanceof Request?input.method:'GET')).toUpperCase();
    if(url.origin!==location.origin || method!=='POST' || !/\/cart\/(add|change|update)\.js$/.test(url.pathname))return nativeFetch(input,options);
    const mutationEpoch=closeEpoch;
    return enqueue(async()=>{
      if(pending())current=await restore(pending());
      const response=await nativeFetch(input,options);
      if(!response.ok)return response;
      setBusy(true);
      try{current=await reconcile(await read());failure='';render();}
      catch(error){failure=error.message;render();}
      finally{setBusy(false);}
      if(/\/add\.js$/.test(url.pathname) && current && closeEpoch===mutationEpoch)queueMicrotask(()=>open());
      if(/\/(change|update)\.js$/.test(url.pathname) && current && !failure){
        const fresh=await nativeFetch(config.root+'cart.js',{cache:'no-store'});
        if(fresh.ok)return new Response(await fresh.text(),{status:response.status,headers:response.headers});
      }
      return response;
    });
  };
  window.addEventListener('click',event=>{
    const target=event.target.closest('a[href],button,.easc-stickyCartDiv');
    if(!target)return;
    if(!target.closest('#monzoro-pocket-cart') && (target.matches('.Cart__Checkout') || (target.closest('.eascMiniCartDiv') && target.textContent.trim()===text('checkout')) || (target.tagName==='A' && new URL(target.href,location.href).pathname===config.root+'checkout'))){
      event.preventDefault();event.stopImmediatePropagation();checkout();return;
    }
    const isCart=target.matches('.easc-stickyCartDiv,[data-action="open-drawer"][data-drawer-id="sidebar-cart"]') || (target.tagName==='A' && new URL(target.href,location.href).pathname===config.root+'cart');
    if(!isCart)return;
    event.preventDefault();event.stopImmediatePropagation();
    open();
  },true);
  document.addEventListener('submit',event=>{
    if(event.submitter?.name!=='checkout')return;
    event.preventDefault();event.stopImmediatePropagation();checkout();
  },true);
  ensure().catch(()=>{});
})();





